import type { PolicyAddressViewModel } from 'src/api/types/PolicyAddressViewModel';

import { useParams } from 'react-router';
import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';
import { memo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';

import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Card,
  Chip,
  Table,
  Divider,
  TableRow,
  Collapse,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  CardContent,
  TableContainer,
} from '@mui/material';

import { usePolicyNamesAndAddresses } from 'src/api/hooks/usePolicyDetailsQueries';

import { TableNoData } from 'src/components/table';

import { scrollbarStyles } from 'src/sections/employer-data/shared/styles/sharedStyles';
import {
  formatDate,
  getAddressToString,
} from 'src/sections/employer-data/shared/utils/policyUtils';

import { InfoBox } from '../../shared/components/info-box';
import { useHeaderWidth } from '../../shared/hooks/useHeaderWidth';
import { NamesAndAddressesEdit } from './names-and-addresses-edit';
import {
  hasNamesAndAddressesChanges,
  buildNamesAndAddressesPayload,
  validateNamesAndAddressesDraft,
  getNamesAndAddressesDraftDelta,
  initializeNamesAndAddressesDraft,
  applyNamesAndAddressesDraftDelta,
  getNamesAndAddressesChangedFieldSummaries,
} from './names-and-addresses.helpers';

import type {
  NamesAndAddressesDraft,
  NamesAndAddressesProps,
  NamesAndAddressesHandle,
  NamesAndAddressesFieldErrors,
} from './names-and-addresses.types';

export function InfoBoxWithChip({
  label,
  value,
  testIdLabel,
  testIdValue,
}: {
  label: string;
  value: number | string;
  testIdLabel: string;
  testIdValue: string;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
      <Typography
        component="span"
        sx={{ typography: 'body2', color: 'text.secondary' }}
        data-testid={testIdLabel}
      >
        {label}
      </Typography>

      <Typography
        component="span"
        sx={{ typography: 'subtitle2', color: 'text.primary' }}
        data-testid={testIdValue}
      >
        {value}{' '}
        <Chip
          label="PEO"
          color="info"
          variant="soft"
          size="small"
          data-testid="primary-name-peo-label"
        />
      </Typography>
    </Box>
  );
}

function NameAndAddressTable({ rows, columns }: { rows: any[]; columns: string[] }) {
  return (
    <Box>
      <TableContainer
        sx={(theme) => ({
          mt: 3,
          maxHeight: '25rem',
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1.5,
          transition: 'margin-bottom 0.2s ease',
          ...scrollbarStyles(theme),
        })}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 2,
                }}
              />
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  sx={{
                    width: `${100 / columns.length}%`,
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 2,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, rowIndex) => <Row key={rowIndex} row={row} rowIndex={rowIndex} />)
            ) : (
              <TableNoData notFound data-testid="additional-names-addresses-no-data" />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

type NameAndAddress = {
  insuredName: string;
  effectiveDate: Date;
  expirationDate: Date;
  fein: string;
  addresses: PolicyAddressViewModel[];
};

type RowProps = {
  row: NameAndAddress;
};

function Row({ row, rowIndex }: RowProps & { rowIndex: number }) {
  const open = useBoolean();
  const theme = useTheme();
  const hasAddresses = row?.addresses && row.addresses.length > 0;

  return (
    <>
      <TableRow>
        <TableCell>
          {hasAddresses && (
            <IconButton aria-label="expand row" size="small" onClick={open.onToggle}>
              {open.value ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell data-testid={`insured-name-${row.insuredName}`}>{row.insuredName}</TableCell>
        <TableCell data-testid={`effective-date-${row.effectiveDate}`}>
          {formatDate(row.effectiveDate)}
        </TableCell>
        <TableCell data-testid={`expiration-date-${row.expirationDate}`}>
          {formatDate(row.expirationDate)}
        </TableCell>
        <TableCell data-testid={`fein-${row.fein}`}>{row.fein}</TableCell>
      </TableRow>
      {hasAddresses && (
        <TableRow>
          <TableCell
            sx={{
              paddingBottom: 0,
              paddingTop: 0,
              backgroundColor: open.value
                ? varAlpha(theme.vars.palette.grey['500Channel'], 0.4)
                : 'inherit',
              borderBottom: 'unset',
            }}
            colSpan={6}
          >
            <Collapse in={open.value} timeout="auto" unmountOnExit>
              <Box style={{ paddingBottom: '1rem', paddingTop: '1rem' }}>
                <TableContainer
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                  }}
                >
                  <Grid container sx={{ width: '100%' }}>
                    <Grid size={{ xs: 6 }} sx={{ p: 2, fontWeight: 'fontWeightSemiBold', borderBottom: 1, borderColor: 'divider' }}>Address</Grid>
                    <Grid size={{ xs: 3 }} sx={{ p: 2, fontWeight: 'fontWeightSemiBold', borderBottom: 1, borderColor: 'divider' }}>Effective date</Grid>
                    <Grid size={{ xs: 3 }} sx={{ p: 2, fontWeight: 'fontWeightSemiBold', borderBottom: 1, borderColor: 'divider' }}>Expiration date</Grid>
                    {row?.addresses?.map((address: PolicyAddressViewModel, addressIndex: number) => (
                      <Grid container key={address.policyAddressId} sx={{ width: '100%', borderBottom: 1, borderColor: 'divider' }}>
                        <Grid size={{ xs: 6 }} sx={{ p: 2, wordBreak: 'break-word' }} data-testid={`row-${rowIndex}-address-${addressIndex}`}>
                          {getAddressToString(address.address ?? {})}
                        </Grid>
                        <Grid size={{ xs: 3 }} sx={{ p: 2 }} data-testid={`row-${rowIndex}-effective-date-${addressIndex}`}>
                          {formatDate(address?.address?.addressEffectiveDate)}
                        </Grid>
                        <Grid size={{ xs: 3 }} sx={{ p: 2 }} data-testid={`row-${rowIndex}-expiration-date-${addressIndex}`}>
                          {formatDate(address?.address?.addressExpirationDate)}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </TableContainer>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const NamesAndAddresses = forwardRef<NamesAndAddressesHandle, NamesAndAddressesProps>(
  (
    {
      comboId: propComboId,
      coverageId: propCoverageId,
      policyId: propPolicyId,
      isEditMode = false,
      stateLookups = [],
      initialDraftDelta = null,
      onDraftDeltaChange,
    },
    ref
  ) => {
  const params = useParams<{ comboId: string; coverageId: string; policyId: string }>();
  const comboId = propComboId ?? Number(params.comboId);
  const coverageId = propCoverageId ?? Number(params.coverageId);
  const policyId = propPolicyId ?? Number(params.policyId);
  const headerWidth = useHeaderWidth();

  const { data } = usePolicyNamesAndAddresses(
    comboId,
    coverageId,
    policyId
  );
  const [draft, setDraft] = useState<NamesAndAddressesDraft | null>(null);
  const [snapshot, setSnapshot] = useState<NamesAndAddressesDraft | null>(null);
  const [fieldErrors, setFieldErrors] = useState<NamesAndAddressesFieldErrors>({});
  const initialDraftDeltaRef = useRef(initialDraftDelta);
  const hasAppliedInitialDeltaRef = useRef(false);

  const namesAndAddresses = data?.data;

  useEffect(() => {
    if (namesAndAddresses) {
      setDraft((currentDraft) => {
        if (isEditMode && currentDraft) {
          return currentDraft;
        }

        const freshDraft = initializeNamesAndAddressesDraft(namesAndAddresses);
        if (!hasAppliedInitialDeltaRef.current) {
          hasAppliedInitialDeltaRef.current = true;
          return applyNamesAndAddressesDraftDelta(freshDraft, initialDraftDeltaRef.current);
        }
        return freshDraft;
      });
      setSnapshot(null);
      setFieldErrors({});
    }
  }, [isEditMode, namesAndAddresses]);

  useEffect(() => {
    if (isEditMode && draft) {
      setSnapshot((current) => current ?? draft);
    }
    if (!isEditMode) {
      setSnapshot(null);
      setFieldErrors({});
    }
  }, [draft, isEditMode]);

  useEffect(() => {
    if (!hasAppliedInitialDeltaRef.current || !draft || !namesAndAddresses) return;
    const baseline = snapshot ?? (namesAndAddresses ? initializeNamesAndAddressesDraft(namesAndAddresses) : draft);
    onDraftDeltaChange?.(getNamesAndAddressesDraftDelta(baseline, draft));
  }, [draft, snapshot, namesAndAddresses, onDraftDeltaChange]);

  const effectiveStateLookups = stateLookups;

  useImperativeHandle(ref, () => ({
    validateForSave: () => {
      if (!draft) return false;
      if (!snapshot || !hasNamesAndAddressesChanges(snapshot, draft)) return true;
      const errors = validateNamesAndAddressesDraft(draft, snapshot);
      setFieldErrors(errors);
      return Object.keys(errors).length === 0;
    },
    hasPendingChanges: () => Boolean(snapshot && draft && hasNamesAndAddressesChanges(snapshot, draft)),
    getChangedFieldSummaries: () => (snapshot && draft ? getNamesAndAddressesChangedFieldSummaries(snapshot, draft, effectiveStateLookups) : []),
    getNamesAndAddressesPayload: () => {
      const currentDraft = draft ?? initializeNamesAndAddressesDraft(namesAndAddresses ?? {});
      return buildNamesAndAddressesPayload(snapshot ?? currentDraft, currentDraft);
    },
    commitEdit: () => setSnapshot(draft),
    cancelEdit: () => {
      if (snapshot) setDraft(snapshot);
      setFieldErrors({});
    },
  }), [draft, effectiveStateLookups, namesAndAddresses, snapshot]);

  return (
    <Card
      sx={[
        () => ({
          display: 'flex',
          zIndex: 'unset',
          overflow: 'unset',
          mt: 5,
          borderRadius: 2,
          boxShadow: 'var(--customShadows-card)',
          width: headerWidth,
        }),
      ]}
    >
      <CardContent sx={{ flexGrow: 1, padding: '0rem' }} data-testid="policy-name-addresses-card">
        {isEditMode && draft ? (
          <NamesAndAddressesEdit
            draft={draft}
            setDraft={setDraft as React.Dispatch<React.SetStateAction<NamesAndAddressesDraft>>}
            stateLookups={effectiveStateLookups}
            fieldErrors={fieldErrors}
          />
        ) : (
          <>
        <Box sx={{ padding: '1.5rem' }}>
          <Typography
            variant="h5"
            sx={{ color: 'text.primary' }}
            data-testid="primary-names-addresses-title"
          >
            Primary names and addresses
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ color: 'text.primary', paddingTop: '1.5rem' }}
            data-testid="employer-title"
          >
            Employer
          </Typography>

          <Grid container spacing={2} columns={3}>
            <Grid size={{ xs: 3, md: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
                <Typography
                  component="span"
                  sx={{ typography: 'body2', color: 'text.secondary' }}
                  data-testid="primary-name-label"
                >
                  Primary name
                </Typography>

                <Typography
                  component="span"
                  sx={{ typography: 'subtitle2', color: 'text.primary' }}
                  data-testid="primary-name-value"
                >
                  {namesAndAddresses?.employerNameAndAddress?.name || ''}{' '}
                  {namesAndAddresses?.employerNameAndAddress?.isPEO && (
                    <Chip
                      label="PEO"
                      color="info"
                      variant="soft"
                      size="small"
                      data-testid="primary-name-peo-label"
                    />
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Mailing address"
                value={getAddressToString(
                  namesAndAddresses?.employerNameAndAddress?.mailingAddress?.address
                )}
                testIdLabel="mailing-address-label"
                testIdValue="mailing-address-value"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="FEIN"
                value={namesAndAddresses?.employerNameAndAddress?.feinLastFive || ''}
                testIdLabel="primary-fein-label"
                testIdValue="primary-fein-value"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Address of operations"
                value={
                  namesAndAddresses?.employerNameAndAddress?.isMultiple
                    ? 'Multiple'
                    : getAddressToString(
                        namesAndAddresses?.employerNameAndAddress?.addressOfOperations?.address
                      )
                }
                testIdLabel="address-of-operations-label"
                testIdValue="address-of-operations-value"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} columns={3}>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Email address"
                value={namesAndAddresses?.employerNameAndAddress?.emailAddress || ''}
                testIdLabel="employer-email-address-label"
                testIdValue="names-and-addresses-employer-email-value"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderStyle: 'dashed', marginLeft: '1.5rem', marginRight: '1.5rem' }} />

        <Box sx={{ padding: '1.5rem' }}>
          <Typography
            variant="subtitle1"
            sx={{ color: 'text.primary' }}
            data-testid="carrier-title"
          >
            Carrier
          </Typography>

          <Grid container spacing={2} columns={3}>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Carrier name"
                value={`${namesAndAddresses?.carrierName || ''} (ID: ${namesAndAddresses?.carrierId || ''})`}
                testIdLabel="carrier-name-label"
                testIdValue="names-and-addresses-carrier-name-value"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Mailing address"
                value={getAddressToString(namesAndAddresses?.carrierAddress?.address)}
                testIdLabel="carrier-mailing-address-label"
                testIdValue="carrier-mailing-address-value"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderStyle: 'dashed', marginLeft: '1.5rem', marginRight: '1.5rem' }} />

        <Box sx={{ padding: '1.5rem' }}>
          <Typography variant="subtitle1" sx={{ color: 'text.primary' }} data-testid="agency-title">
            Agency
          </Typography>

          <Grid container spacing={2} columns={3}>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Agency name"
                value={namesAndAddresses?.agencyNameAndAddress?.name || ''}
                testIdLabel="agency-name-label"
                testIdValue="agency-name-value"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Mailing address"
                value={getAddressToString(
                  namesAndAddresses?.agencyNameAndAddress?.mailingAddress?.address
                )}
                testIdLabel="agency-mailing-address-label"
                testIdValue="agency-mailing-address-value"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} columns={3}>
            <Grid size={{ xs: 3, md: 1 }}>
              <InfoBox
                label="Email address"
                value={namesAndAddresses?.agencyNameAndAddress?.emailAddress || ''}
                testIdLabel="names-and-addresses-agency-email-label"
                testIdValue="names-and-addresses-agency-email-value"
              />
            </Grid>
          </Grid>
        </Box>

        <Divider
          sx={{
            borderStyle: 'dashed',
            marginLeft: '1.5rem',
            marginRight: '1.5rem',
            marginBottom: '1.5rem',
          }}
        />

        <Box sx={{ padding: '1.5rem' }}>
          <Typography
            variant="h5"
            sx={{ color: 'text.primary' }}
            data-testid="additional-names-addresses-title"
          >
            Additional names and addresses
          </Typography>
          <NameAndAddressTable
            columns={['Employer name', 'Effective date', 'Expiration date', 'FEIN']}
            rows={
              namesAndAddresses?.additionalNamesAndAddresses?.map((nameAndAddress) => ({
                insuredName: nameAndAddress?.employerName,
                effectiveDate: formatDate(nameAndAddress.policyEffectiveDate),
                expirationDate: formatDate(nameAndAddress.policyExpirationDate),
                fein: nameAndAddress.feinLastFive,
                addresses: nameAndAddress.addresses || [],
              })) || []
            }
          />
        </Box>
        <Divider sx={{ borderStyle: 'dashed', marginLeft: '1.5rem', marginRight: '3rem' }} />
          </>
        )}
      </CardContent>
    </Card>
  );
});

NamesAndAddresses.displayName = 'NamesAndAddresses';

export default memo(NamesAndAddresses);
